import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { ServicePlanItem } from 'src/features/servicePlanItems/utils/types';
import {
    servicePlanItemFormSchema,
    ServicePlanItemFormSchemaValues,
} from 'src/features/servicePlanItems/utils/formSchema';
import { useGetServicePlanItem } from 'src/features/servicePlanItems/api/useGetServicePlanItem';
import ServicePlanItemForm from 'src/features/servicePlanItems/components/forms/ServicePlanItemForm';
import { useParams } from 'react-router-dom';

interface ServicePlanItemFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanItem?: ServicePlanItem;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: ServicePlanItemFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function ServicePlanItemFormModal<TData>({
    selectedServicePlanItem,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: ServicePlanItemFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { servicePlanId = '' } = useParams();

    const { data: servicePlanItem } = useGetServicePlanItem({
        id: selectedServicePlanItem?.id ?? '',
        config: { enabled: !!selectedServicePlanItem?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            name: servicePlanItem?.name ?? '',
            code: servicePlanItem?.code ?? '',
            is_required: servicePlanItem?.is_required ?? undefined,
            billable_metric: servicePlanItem?.billable_metric ?? undefined,
            external_item_id: servicePlanItem?.external_item_id ?? undefined,
            is_active: servicePlanItem?.is_active ?? undefined,
            service_plan: servicePlanItem?.service_plan ?? servicePlanId ?? undefined,
            starting_quantity: servicePlanItem?.starting_quantity ?? undefined,
            allows_price_coefficient: servicePlanItem?.allows_price_coefficient ?? false,
            unit: servicePlanItem?.unit?.id ?? undefined,
        };
    }, [servicePlanItem, servicePlanId]);

    const form = useForm<ServicePlanItemFormSchemaValues>({
        resolver: zodResolver(servicePlanItemFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ServicePlanItemFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ServicePlanItemForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedServicePlanItem && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ServicePlanItemFormModal;
